import { Location } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProgressSpinnerComponent } from '../helper/anessa-library/anessa-library/projects/anessa-components/src/public-api';
import { CommunicationService } from '../communication/communication.service';
import { ProjectCommunicationService } from '../communication/project-comms.service';
import { DeactivationDialogService } from '../helper/can-deactivate/deactivation-dialog.service';
import { User, Permissions, PermissionsInterface } from '../models/user.model';
import Util from '../util';
import { AnessaTranslationService } from '../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../helper/userpilot-reloader.component';
import { AccAdminProjectInfo, PermissionType, PermissionValues, UserPermissions } from '../models/acc-admin/acc-admin.model';

@Component({
  selector: 'app-acc-admin',
  templateUrl: './acc-admin.component.html',
  styleUrls: ['./acc-admin.component.scss']
})
export class AccAdminComponent extends UserpilotReloader implements OnInit, OnDestroy {
  private onDestroyNotifier = new EventEmitter();
  /** trigger for translation pipe */
  trigger?: string;

  isDirty = false;
  usersDone = false;
  projectsDone = false;

  checkboxTooltipText?: string;

  projects: AccAdminProjectInfo[] = [];
  users: User[] = [];

  projectFilter: string = '';

  filteredProjects: AccAdminProjectInfo[] = [];
  filteredUsers: User[] = [];

  selectedUser?: User;
  selectedProject?: AccAdminProjectInfo;
  selectedPermissions?: UserPermissions;

  permissionCount = 0;
  permissionNames: string[] = [];
  permissionTypes: PermissionType[] = ['canEditInitialInformation', 'canView', 'canEdit', 'canSimulate', 'canDelete'];
  permissionValues: PermissionValues = {
    canView: false,
    canEdit: false,
    canSimulate: false,
    canDelete: false,
    canEditInitialInformation: false,
    readOnlyInitialChecklists: false,
    editOnlyInitialChecklists: false
  };
  permissionsData: UserPermissions[] = [];

  constructor(
    private snackBar: MatSnackBar,
    private commService: CommunicationService,
    private projectCommService: ProjectCommunicationService,
    private toastrService: ToastrService,
    private location: Location,
    private dialogService: DeactivationDialogService,
    public translationService: AnessaTranslationService
  ) {
    super();
  }

  clearProjectFilter() {
    this.projectFilter = '';
    this.filteredProjects = this.projects;
  }

  filterProjects() {
    if (this.projectFilter && this.projectFilter !== '') {
      this.filteredProjects = this.projects.filter(project => {
        return project.title.toLowerCase().includes(this.projectFilter.toLowerCase());
      });
    } else {
      this.filteredProjects = this.projects;
    }
  }

  filterUsers(searchTerm: string) {
    if (searchTerm != null) {
      this.filteredUsers = this.users.filter(user => {
        return user.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    } else {
      this.filteredUsers = this.users;
    }
  }

  /**
   * Prevent removing the view permission of the currently opened project from the current user
   */
  shouldDisablePermissionCheckbox(permissionType: string) {
    return (
      permissionType === 'canView'
      && this.selectedUser?.id === localStorage.getItem('userID')
      && this.selectedProject?.id === localStorage.getItem('projectID')
    );
  }

  /**
   * Display a tooltip if the checkbox is disabled
   */
  getCheckboxTooltip(permissionType: string) {
    if (this.shouldDisablePermissionCheckbox(permissionType)) {
      return this.checkboxTooltipText;
    } else {
      return null;
    }
  }

  /**
   * removes potential duplicates from array and cleans up rights
   *
   */
  prepareSendPackage() {
    for (const user of this.users) {
      const userPerms = this.permissionsData.find(p => p.userId === user.id);
      if (!userPerms) continue;
      const projectIds = [
        ...userPerms.canView,
        ...userPerms.canEdit,
        ...userPerms.canSimulate,
        ...userPerms.canDelete,
        ...userPerms.canEditInitialInformation,
      ];
      const uniqueProjectIds = new Set(projectIds);

      user.adaPermissions = [];
      for (const projectId of uniqueProjectIds) {
        user.adaPermissions.push(new Permissions({
          projectId: projectId,
          canView: userPerms.canView.includes(projectId),
          canEdit: userPerms.canEdit.includes(projectId),
          canSimulate: userPerms.canSimulate.includes(projectId),
          canDelete: userPerms.canDelete.includes(projectId),
          canEditInitialInformation: userPerms.canEditInitialInformation.includes(projectId),
          readOnlyInitialChecklists: userPerms.readOnlyInitialChecklists.includes(projectId),
          editOnlyInitialChecklists: userPerms.editOnlyInitialChecklists.includes(projectId)
        }));
      }
    }
  }


  onSave() {
    this.prepareSendPackage();
    const saveSub = this.commService.postRequest(this.users, '/wsasapi/accadmin/savePermissions')
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(
        data => {
          if (data) {
            this.isDirty = false;
            this.toastrService.success(this.translationService.get('ACC_ADMIN.SETTINGS_SAVED'));
            if (Util.isProjectLoaded()) {
              // Update user permissions if project loaded
              this.getUserPermissionsRequest()
                .pipe(takeUntil(this.onDestroyNotifier))
                .subscribe(
                  response => {
                    const permissions: Permissions[] = response['obj'].map(permission => new Permissions(permission));
                    const projectPerms = permissions.find(p => p.projectId === localStorage.getItem('projectID'));
                    Util.setProjectPermissions(
                      projectPerms?.canEdit || false,
                      projectPerms?.canSimulate || false,
                      projectPerms?.canDelete || false,
                      projectPerms?.canEditInitialInformation || false,
                      projectPerms?.readOnlyInitialChecklists || false,
                      projectPerms?.editOnlyInitialChecklists || false
                    );
                  }
                );
            }
            if (saveSub) {
              saveSub.unsubscribe();
            }
          }
        }
      );
  }

  getUserName(userId: string) {
    const user = this.users.find(u => u.id === userId);
    if (user) {
      return user.name;
    }
    return null;
  }

  addPermission(permissionType: PermissionType) {
    if (!this.selectedProject || !this.selectedPermissions) return;
    if (!this.selectedPermissions[permissionType].includes(this.selectedProject.id)) {
      (this.selectedPermissions[permissionType] as string[]).push(this.selectedProject.id);
      this.permissionValues[permissionType] = true;
    }
  }

  removePermission(permissionType: PermissionType) {
    if (!this.selectedProject || !this.selectedPermissions) return;
    if (this.selectedPermissions[permissionType].includes(this.selectedProject.id)) {
      this.selectedPermissions[permissionType].splice(this.selectedPermissions[permissionType].indexOf(this.selectedProject.id), 1);
      this.permissionValues[permissionType] = false;
    }
  }

  onUserChange() {
    if (this.selectedProject) {
      this.onProjectChange(this.selectedProject);
    }
  }

  onProjectChange(project: any) {
    this.selectedPermissions = this.permissionsData.find(p => p.userId === this.selectedUser?.id);
    if (!this.selectedPermissions) return;
    this.selectedProject = project;

    this.permissionValues.canView = this.selectedPermissions.canView.includes(project.id);
    this.permissionValues.canEdit = this.selectedPermissions.canEdit.includes(project.id);
    this.permissionValues.canSimulate = this.selectedPermissions.canSimulate.includes(project.id);
    this.permissionValues.canDelete = this.selectedPermissions.canDelete.includes(project.id);
    this.permissionValues.canEditInitialInformation = this.selectedPermissions.canEditInitialInformation.includes(project.id);
  }

  /**
   * Grants or revokes any permissions that are dependent on the permission
   * being modified.
   *
   * @param permissionType type of permission being granted or revoked
   * @param isAdd whether the permissions is being granted or revoked
   */
  onPermissionChange(permissionType: string, isAdd: boolean) {
    switch (permissionType) {
      case 'canView':
        if (isAdd) {
          this.addPermission('canView');
          this.removePermission('canEditInitialInformation');
        } else {
          this.removePermission('canView');
          this.removePermission('canEdit');
          this.removePermission('canSimulate');
          this.removePermission('canDelete');
        }
        break;
      case 'canEdit':
        if (isAdd) {
          this.addPermission('canView');
          this.addPermission('canEdit');
          this.removePermission('canEditInitialInformation');
        } else {
          this.removePermission('canEdit');
          this.removePermission('canSimulate');
          this.removePermission('canDelete');
        }
        break;
      case 'canSimulate':
        if (isAdd) {
          this.addPermission('canView');
          this.addPermission('canEdit');
          this.addPermission('canSimulate');
          this.removePermission('canEditInitialInformation');
        } else {
          this.removePermission('canSimulate');
          this.removePermission('canDelete');
        }
        break;
      case 'canDelete':
        if (isAdd) {
          this.addPermission('canView');
          this.addPermission('canEdit');
          this.addPermission('canSimulate');
          this.addPermission('canDelete');
          this.removePermission('canEditInitialInformation');
        } else {
          this.removePermission('canDelete');
        }
        break;
      case 'canEditInitialInformation':
        if (isAdd) {
          this.addPermission('canEditInitialInformation');
          this.removePermission('canView');
          this.removePermission('canEdit');
          this.removePermission('canSimulate');
          this.removePermission('canDelete');
        } else {
          this.removePermission('canEditInitialInformation');
        }
    }
  }

  prepareDataSet() {
    this.permissionsData = [];
    for (const user of this.users) {
      const userPerms: UserPermissions = {
        userId: user.id,
        canView: [],
        canEdit: [],
        canSimulate: [],
        canDelete: [],
        canEditInitialInformation: [],
        readOnlyInitialChecklists: [],
        editOnlyInitialChecklists: []
      };
      for (const project of this.projects) {
        const permission = user.adaPermissions.find(p => p.projectId === project.id);
        for (const type of this.permissionTypes) {
          if (permission && permission[type]) {
            userPerms[type].push(project.id);
            this.permissionCount++;
          }
        }
      }
      this.permissionsData.push(userPerms);
    }
  }

  checkDone() {
    if (this.usersDone && this.projectsDone) {
      this.prepareDataSet();
      this.snackBar.dismiss();
      this.filteredProjects = this.projects;
      this.filteredUsers = this.users;
    }
  }

  cancel() {
    this.isDirty = false;
    this.location.back();
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.isDirty) {
      return this.dialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'));
    }
    return true;
  }

  setTranslations() {
    this.checkboxTooltipText = this.translationService.get('ACC_ADMIN.TOOLTIP');
    this.permissionNames = [
      this.translationService.get('ACC_ADMIN.INITIAL'),
      this.translationService.get('ACC_ADMIN.VIEW'),
      this.translationService.get('ACC_ADMIN.EDIT'),
      this.translationService.get('ACC_ADMIN.SIMULATE'),
      this.translationService.get('ACC_ADMIN.DELETE')
    ];
    // Remove initial information permission checkbox
    if (!Util.canViewProjectInput()) {
      this.permissionNames.splice(0, 1);
    }
  }

  ngOnInit() {
    this.projectCommService.unloadProject();
    this.setTranslations();

    this.snackBar.openFromComponent(ProgressSpinnerComponent, {
      data: this.translationService.get('ACC_ADMIN.LOADING_PERMS'),
      panelClass: ['anessa-snack-bar-container'],
    });

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.setTranslations();
        this.trigger = this.translationService.getCurrentLang();
      });

    this.getInfoRequest()
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(
        result => {
          for (const user of result['obj']) {
            if (user.userrole === 'initialformuser') {
              continue;
            }
            this.users.push(new User(user));
          }
          this.usersDone = true;
          this.checkDone();
        }
      );

    this.getNamesRequest()
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(
        data => {
          this.projects = data['obj'];
          this.projects.sort(function (a, b) {
            const x = a.title;
            const y = b.title;
            if (x < y) { return -1; }
            if (x > y) { return 1; }
            return 0;
          });

          this.projectsDone = true;
          this.checkDone();
        }
      );
  }

  getInfoRequest() {
    type reqtype = Observable<{obj: User[]}>;
    return <reqtype>this.commService.getAllRequest('/wsasapi/accadmin/getInfo?id=' + localStorage.getItem('accountID'));
  }

  getNamesRequest() {
    type reqtype = Observable<{obj: AccAdminProjectInfo[]}>
    return <reqtype>this.commService.getAllRequest('/wsasapi/accadmin/getnames');
  }

  getUserPermissionsRequest() {
    type reqtype = Observable<{obj: PermissionsInterface[]}>;
    return <reqtype>this.commService.getAllRequest('/wsasapi/data/getUserPermissions?id=' + localStorage.getItem('userID'));
  }

  ngOnDestroy() {
    this.snackBar.dismiss();
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }
}
